import { transformDocumentNumber } from '@/components/custom/erp';
import type { FormFieldState } from '../hooks/useFormFieldState';
import { MA_CHUNG_TU } from '@/constants/ma-chung-tu';
import { isValidUUID } from '@/lib/uuid-validator';
import type { FormMode } from '@/types/form';

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (detailRows: MuaHangRecord[], formMode: FormMode) => {
  return detailRows?.map((row: MuaHangRecord, index: number) => {
    const baseData = {
      line: index + 1,
      ma_dv: row.ma_dv_data?.uuid || null,
      tk_vt: row.tk_vt_data?.uuid || row.ma_dv_data?.tk_cp_data?.uuid || null,
      dien_giai: row.dien_giai || row.ma_dv_data?.ten_dv || null,
      ma_lts: row.ma_lts_data?.uuid || null,
      dvt: row.dvt_data?.uuid || row.dvt || row.ma_dv_data?.dvt_data?.uuid || row.ma_dv_data?.dvt || null,
      so_luong: row.so_luong || 0,
      gia_nt: row.gia_nt || row.ma_dv_data?.gia_nt || 0.0,
      gia: row.gia || 0.0,
      tien_nt: row.tien_nt || 0.0,
      tien: row.tien || 0.0,
      tien_tck_nt: row.tien_tck_nt || 0.0,
      tl_ck: row.tl_ck || 0.0,
      ck_nt: row.ck_nt || 0.0,
      tien_tck: row.tien_tck || 0.0,
      ck: row.ck || 0.0,
      ma_thue: row.ma_thue_data?.uuid || row.ma_dv_data?.ma_thue || null,
      thue_nt: row.thue_nt || 0.0,
      thue_suat: row.ma_thue_data?.thue_suat || row.ma_dv_data?.thue_suat_data?.thue_suat || 0.0,
      thue: row.thue_nt || 0.0,
      ma_bp: row.ma_bp_data?.uuid || row.ma_dv_data?.ma_bp_data?.uuid || null,
      ma_vv: row.ma_vv_data?.uuid || row.ma_dv_data?.ma_vv_data?.uuid || null,
      ma_hd: row.ma_hd_data?.uuid || row.ma_dv_data?.ma_hd_data?.uuid || null,
      ma_dtt: row.ma_dtt_data?.uuid || row.ma_dv_data?.ma_dtt_data?.uuid || null,
      ma_ku: row.ma_ku_data?.uuid || row.ma_dv_data?.ma_ku_data?.uuid || null,
      ma_phi: row.ma_phi_data?.uuid || row.ma_dv_data?.ma_phi_data?.uuid || null,
      ma_sp: row.ma_sp_data?.uuid || null,
      ma_lsx: row.ma_lsx_data?.uuid || null,
      ma_cp0: row.ma_cp0_data?.uuid || null
    };

    // Chỉ thêm uuid nếu formMode !== 'add' và uuid hợp lệ
    if (formMode !== 'add' && isValidUUID(row.uuid)) {
      return { uuid: row.uuid, ...baseData };
    }

    return baseData;
  });
};

/**
 * Transform tax rows for API submission
 * @param taxRows - Array of tax row data from the form
 * @returns Transformed tax rows ready for API submission
 */
export const transformTaxData = (
  taxRows: MuaHangRecord[],
  formMode: FormMode,
  state: FormFieldState,
  dien_giai: string
) => {
  return taxRows?.map((row: MuaHangRecord, index: number) => {
    const baseData = {
      line: index + 1,
      so_ct0: row.so_ct0 || null,
      so_ct2: row.so_ct2 || null,
      ngay_ct0: row.ngay_ct0 || null,
      ma_mau_ct: row.ma_mau_ct_data?.uuid || null,
      ma_mau_bc: row.ma_mau_bc || '3',
      ma_tc_thue: row.ma_tc_thue_data?.uuid || null,
      ma_kh: row.ma_kh_data?.uuid || state.nhaCungCap?.uuid || null,
      ten_kh_thue: row.ma_kh_data?.customer_name || state.nhaCungCap?.customer_name || row.ten_kh_thue || null,
      dia_chi: row.dia_chi || state.nhaCungCap?.address || row.ma_kh_data?.address || null,
      ma_so_thue: row.ma_so_thue || state.nhaCungCap?.tax_code || row.ma_kh_data?.tax_code || null,
      ten_vt_thue: row.ten_vt_thue || dien_giai || null,
      t_tien_nt: row.t_tien_nt || 0.0,
      t_tien: row.t_tien || 0.0,
      t_thue_nt: row.t_thue_nt || 0.0,
      t_thue: row.t_thue || 0.0,
      tk_thue_no: row.tk_thue_no_data?.uuid || null,
      ma_thue: row.ma_thue_data?.uuid,
      thue_suat: row.ma_thue_data?.thue_suat || 0.0,
      ma_kh9: row.ma_kh9_data?.uuid || null,
      ghi_chu: row.ghi_chu || null,
      ma_bp: row.ma_bp_data?.uuid || null,
      ma_vv: row.ma_vv_data?.uuid || null,
      ma_hd: row.ma_hd_data?.uuid || null,
      ma_dtt: row.ma_dtt_data?.uuid || null,
      ma_ku: row.ma_ku_data?.uuid || null,
      ma_phi: row.ma_phi_data?.uuid || null,
      ma_sp: row.ma_sp_data?.uuid || null,
      ma_lsx: row.ma_lsx_data?.uuid || null,
      ma_cp0: row.ma_cp0_data?.uuid || null
    };

    // Chỉ thêm uuid nếu formMode !== 'add' và uuid hợp lệ
    if (formMode !== 'add' && isValidUUID(row.uuid)) {
      return { uuid: row.uuid, ...baseData };
    }

    return baseData;
  });
};

/**
 * Transform TSCC rows for API submission
 * @param tscRows - Array of TSCC row data from the form
 * @returns Transformed TSCC rows ready for API submission
 */
export const transformTSCCData = (tscRows: MuaHangRecord[], formMode: FormMode, state?: FormFieldState) => {
  return tscRows?.map((row: MuaHangRecord, index: number) => {
    const baseData = {
      line: index + 1,
      line_ct: row.detail_line ?? row.line_ct ?? row.line ?? null,

      // Thông tin tài sản cơ bản
      ma_ts: row.ma_ts || null,
      ten_ts: row.ten_ts || null,
      ma_lts: row.ma_lts_data?.uuid || null,

      // Thông tin thời gian
      ngay_mua: row.ngay_mua || state?.ngay_ct || null,
      ngay_kh0: row.ngay_kh0 || state?.ngay_ct || null,
      ngay_kh_kt: row.ngay_kh_kt || null,
      so_ky_kh: row.so_ky_kh || null,

      // Thông tin giá trị tài chính
      nguyen_gia_nt: row.nguyen_gia_nt || 0,
      gt_da_kh_nt: row.gt_da_kh_nt || 0,
      gt_cl_nt: row.gt_cl_nt || 0,
      gt_kh_ky_nt: row.gt_kh_ky_nt || 0,

      // Thông tin khác
      so_hieu_ts: row.so_hieu_ts || null,
      dvt: row.dvt_data?.uuid || row.ma_dv_data?.dvt_data?.uuid || row.dvt || null,
      so_luong: row.so_luong || 0,

      // Thông tin tài khoản
      tk_ts: row.tk_ts_data?.uuid || null,
      tk_kh: row.tk_kh_data?.uuid || null,
      tk_cp: row.tk_cp_data?.uuid || null,

      // Thông tin phân bổ
      ma_bp: row.ma_bp_data?.uuid || null,
      ma_vv: row.ma_vv_data?.uuid || null,
      ma_phi: row.ma_phi_data?.uuid || null
    };

    // Chỉ thêm uuid nếu formMode !== 'add' và uuid hợp lệ
    if (formMode !== 'add' && isValidUUID(row.uuid)) {
      return { uuid: row.uuid, ...baseData };
    }

    return baseData;
  });
};

/**
 * Transform all form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @param taxRows - Array of tax row data
 * @param tscRows - Array of TSCC row data
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: MuaHangRecord,
  state: FormFieldState,
  formMode: FormMode,
  tong_thue: number,
  tong_tien: number,
  tong_chiet_khau: number,
  tong_thanh_toan: number,
  ...rest: MuaHangRecord[]
) => {
  // Transform detail and account rows
  const detail = transformDetailRows(rest[0], formMode);
  const tax = transformTaxData(rest[1], formMode, state, data.dien_giai);
  const tai_san = transformTSCCData(rest[2] || [], formMode, state);

  return {
    ...data,
    // Form state checkboxes
    ma_gd: state.ma_gd,
    pc_tao_yn: state.pc_tao_yn,
    ck_yn: state.ck_yn,
    ck_tl_nt: state.chietKhau || 0,
    loai_ck: state.loai_ck,

    // Payment method from state
    ma_httt: state.pc_tao_yn === false ? null : state.ma_httt,

    // Customer information
    ma_kh: state.nhaCungCap?.uuid || null,
    ma_so_thue: data.ma_so_thue || state.nhaCungCap?.tax_code || null,
    ten_kh_thue: data.ten_kh_thue || state.nhaCungCap?.customer_name || null,
    dia_chi: data.dia_chi || state.nhaCungCap?.address || null,
    ong_ba: data.ong_ba || state.nhaCungCap?.contact_person || null,
    e_mail: data.e_mail || state.nhaCungCap?.email || null,
    dien_giai: data.dien_giai || state.nhaCungCap?.description || null,
    so_ct0: data.so_ct0 || null,

    // References
    ma_nvmh: state.nhanVien?.uuid || state.nhaCungCap?.sales_rep_data?.uuid || null,
    tk: state.taiKhoan?.uuid || state.nhaCungCap?.account_data?.uuid || null,
    ma_tt: state.hanThanhToan?.uuid || state.nhaCungCap?.payment_term_data?.uuid || null,

    // Document information
    ma_ngv: '1',
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu, MA_CHUNG_TU.MUA_HANG.HOA_DON_MUA_DICH_VU),
    so_ct2: data.so_ct2 || null,
    ngay_ct: data.ngay_ct || null,
    ngay_lct: data.ngay_ct || null,

    // Totals
    t_tien_nt: tong_tien,
    t_tien: tong_tien,
    t_thue_nt: tong_thue,
    t_thue: tong_thue,
    t_tt_nt: tong_thanh_toan,
    t_tt: tong_thanh_toan,
    t_ck_nt: tong_chiet_khau,
    t_ck: tong_chiet_khau,

    // Detail arrays
    chi_tiet: detail,
    thue: tax,
    tai_san: tai_san
  };
};

export const transformToKhaiBaoTSCD = (invoiceData: MuaHangRecord): MuaHangRecord[] => {
  const makePayload = (src: MuaHangRecord) => ({
    ma_ts: src.ma_ts || invoiceData.ma_ts || '',
    ten_ts: src.ten_ts || invoiceData.ten_ts || '',
    kieu_kh: '0',

    gt_cl_nt: src.gt_cl_nt ?? invoiceData.gt_cl_nt ?? 0,
    gt_da_kh_nt: src.gt_da_kh_nt ?? invoiceData.gt_da_kh_nt ?? 0,
    gt_kh_ky_nt: src.gt_kh_ky_nt ?? invoiceData.gt_kh_ky_nt ?? 0,
    nguyen_gia_nt: src.nguyen_gia_nt ?? invoiceData.nguyen_gia_nt ?? 0,

    so_luong: src.so_luong || invoiceData.so_luong || 0,

    ma_bp: src.ma_bp || invoiceData.ma_bp || '',
    ma_lts: src.ma_lts || invoiceData.ma_lts || '',

    ma_nt: invoiceData.ma_nt || '',
    ty_gia: 1,

    ngay_ct: invoiceData.ngay_ct || '',
    ngay_kh0: src.ngay_kh0 || invoiceData.ngay_kh0 || '',
    ngay_kh_nt: src.ngay_kh_nt || invoiceData.ngay_kh_nt || '',
    ngay_kh_kt: src.ngay_kh_kt || invoiceData.ngay_kh_kt || src.ngay_kh_nt || invoiceData.ngay_kh_nt || '',
    ngay_mua: src.ngay_mua || invoiceData.ngay_mua || '',

    ty_le_kh: 0,

    tk_cp: src.tk_cp || invoiceData.tk_cp || '',
    tk_kh: src.tk_kh || invoiceData.tk_kh || '',
    tk_ts: src.tk_ts || invoiceData.tk_ts || '',

    so_ct: invoiceData.so_ct || '',
    unit_id: invoiceData.unit_id || ''
  });

  if (Array.isArray(invoiceData?.tai_san) && invoiceData.tai_san.length > 0) {
    return invoiceData.tai_san.map((row: MuaHangRecord) => makePayload(row));
  }
  return [makePayload(invoiceData)];
};
